import { useState } from 'react'

import { CopyButton } from '@/components/ui/CopyButton'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { Cap, Demo, DsSection } from '../primitives'

/** One labeled state, captioned with the exact prop that produces it. */
function StateCell({ label, value, children }: { label: string; value?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <Cap>{label}</Cap>
      {children}
      {value && (
        <div className="text-ink-400 flex items-center gap-1 font-mono text-[10px]">
          <span>{value}</span>
          <CopyButton
            value={value}
            className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
          />
        </div>
      )}
    </div>
  )
}

export function MultilineFieldSection() {
  const [value, setValue] = useState('')

  return (
    <DsSection id="multiline-field" title="Multiline form field">
      <Demo label="Anatomy — label · textarea · description">
        <Field className="max-w-sm">
          <FieldLabel htmlFor="ml-note">Anything else we should know?</FieldLabel>
          <Textarea id="ml-note" placeholder="Tell us about your operation" />
          <FieldDescription>Your broker reads this before pulling quotes.</FieldDescription>
        </Field>
      </Demo>

      <div>
        <Cap>States</Cap>
        <div className="mt-3 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <StateCell label="Default" value="placeholder">
            <Field>
              <FieldLabel htmlFor="ml-default">Notes</FieldLabel>
              <Textarea id="ml-default" placeholder="Tell us about your operation" />
            </Field>
          </StateCell>

          <StateCell label="Filled" value="defaultValue">
            <Field>
              <FieldLabel htmlFor="ml-filled">Notes</FieldLabel>
              <Textarea
                id="ml-filled"
                defaultValue="We run 12 reefers out of Fresno, mostly produce to the Midwest."
              />
            </Field>
          </StateCell>

          <StateCell label="Focus / typing — click in">
            <Field>
              <FieldLabel htmlFor="ml-focus">Notes</FieldLabel>
              <Textarea
                id="ml-focus"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Type here"
              />
            </Field>
          </StateCell>

          <StateCell label="With description" value="<FieldDescription>">
            <Field>
              <FieldLabel htmlFor="ml-desc">Notes</FieldLabel>
              <Textarea id="ml-desc" placeholder="Tell us about your operation" />
              <FieldDescription>No need to repeat anything from your application.</FieldDescription>
            </Field>
          </StateCell>

          {/* Required is the unmarked default, so this is the only marked state. */}
          <StateCell label="Optional" value="optional">
            <Field>
              <FieldLabel htmlFor="ml-optional" optional>
                Notes
              </FieldLabel>
              <Textarea id="ml-optional" placeholder="Tell us about your operation" />
            </Field>
          </StateCell>

          <StateCell label="Disabled" value="disabled">
            <Field data-disabled="true">
              <FieldLabel htmlFor="ml-disabled">Notes</FieldLabel>
              <Textarea id="ml-disabled" placeholder="Tell us about your operation" disabled />
            </Field>
          </StateCell>

          <StateCell label="Invalid / error" value="aria-invalid">
            <Field data-invalid="true">
              <FieldLabel htmlFor="ml-error">Notes</FieldLabel>
              <Textarea id="ml-error" defaultValue="." aria-invalid />
              <FieldError>Give us a sentence or two so your broker can act on it.</FieldError>
            </Field>
          </StateCell>

          <StateCell label="Read-only" value="readOnly">
            <Field>
              <FieldLabel htmlFor="ml-readonly">Notes</FieldLabel>
              <Textarea
                id="ml-readonly"
                readOnly
                defaultValue="Submitted 12 Aug — locked while under review."
              />
            </Field>
          </StateCell>
        </div>
      </div>

      <div>
        <Cap>Height — grows with content, from a 64px floor</Cap>
        <div className="mt-3 flex max-w-sm flex-col gap-4">
          <Textarea placeholder="Empty — min-h-16 (64px)" />
          <Textarea
            defaultValue={
              'Grows as you type.\nField-sizing-content means the box tracks its\ncontent instead of scrolling inside a fixed height.'
            }
          />
          <Textarea placeholder="Taller floor" className="min-h-32" />
          <div className="text-ink-400 flex items-center gap-1 font-mono text-[10px]">
            <span>className=&quot;min-h-32&quot;</span>
            <CopyButton
              value='className="min-h-32"'
              className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
            />
          </div>
        </div>
      </div>
    </DsSection>
  )
}
